/**
 * Bonificação Permission Matrix
 *
 * Determines field-level permissions for IndicatorRow based on
 * assessment status, user role, and whether the user is viewing their own assessment.
 *
 * Reference: docs/architecture/BONIFICACAO-ARCHITECTURE.md section 6
 */

export type BonusAssessmentStatus = 'rascunho' | 'em_apuracao' | 'validacao_rh' | 'finalizado'

export interface FieldPermissions {
  valor: boolean
  comprovado: boolean
  rhValidado: boolean
  evidencia: boolean
  evidenceUpload: boolean
  rhComentario: boolean
}

const RH_ROLES = ['admin', 'rh', 'analista-rh']
const DIRECTOR_ROLES = ['diretor-executivo', 'diretor-tecnico']

export function isRHRole(role: string): boolean {
  return RH_ROLES.includes(role)
}

export function isDirectorRole(role: string): boolean {
  return DIRECTOR_ROLES.includes(role)
}

const ALL_LOCKED: FieldPermissions = {
  valor: false,
  comprovado: false,
  rhValidado: false,
  evidencia: false,
  evidenceUpload: false,
  rhComentario: false,
}

export function getFieldPermissions(
  status: BonusAssessmentStatus,
  userRole: string,
  isOwnAssessment: boolean
): FieldPermissions {
  if (status === 'finalizado') {
    return { ...ALL_LOCKED }
  }

  const rh = isRHRole(userRole)
  const director = isDirectorRole(userRole)
  const directorOwn = director && isOwnAssessment

  if (status === 'rascunho') {
    return {
      valor: rh || directorOwn,
      comprovado: rh,
      rhValidado: false,
      evidencia: false,
      evidenceUpload: false,
      rhComentario: false,
    }
  }

  if (status === 'em_apuracao') {
    return {
      valor: rh || directorOwn,
      comprovado: rh,
      rhValidado: false,
      evidencia: rh || directorOwn,
      evidenceUpload: rh || directorOwn,
      rhComentario: false,
    }
  }

  // validacao_rh
  return {
    valor: rh,
    comprovado: rh,
    rhValidado: rh,
    evidencia: rh,
    evidenceUpload: rh,
    rhComentario: rh,
  }
}

/**
 * Action-level permissions for status workflow buttons and field-level actions.
 *
 * Reference: docs/architecture/BONIFICACAO-ARCHITECTURE.md section 6
 */
export type StatusAction = 'advanceStatus' | 'revertStatus' | 'evidence'

export function canEdit(
  action: StatusAction,
  status: BonusAssessmentStatus,
  userRole: string
): boolean {
  const rh = isRHRole(userRole)
  const director = isDirectorRole(userRole)

  if (action === 'advanceStatus') {
    switch (status) {
      case 'rascunho':
        return rh || director
      case 'em_apuracao':
        return rh
      case 'validacao_rh':
        return rh
      case 'finalizado':
        return false
    }
  }

  if (action === 'revertStatus') {
    switch (status) {
      case 'rascunho':
        return false
      case 'em_apuracao':
        return rh
      case 'validacao_rh':
        return rh
      case 'finalizado':
        return rh
    }
  }

  if (action === 'evidence') {
    switch (status) {
      case 'rascunho':
        return rh || director
      case 'em_apuracao':
        return rh || director
      case 'validacao_rh':
        return rh
      case 'finalizado':
        return false
    }
  }

  return false
}
